import { Button, Grid } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { useFormik } from "formik";
import * as Yup from "yup";
import {
  HCPCS_CODE_CATALOG_INITIAL_VALUES,
  hcpcsCodeCatalogInitialValuesType,
} from "../../../../../constants/initial-schemas";
import { DisclosureHost } from "../../../../../hooks/use-disclosure";
import { useLoading } from "../../../../../hooks/use-loading";
import { useSnackbar } from "../../../../../hooks/useSnackbar";
import { useAddCataLogCode } from "../../../../../queries/catalog/use-add-catalog-code";
import { useUpdateCataLogCode } from "../../../../../queries/catalog/use-update-catalog-code";
import {
  getErrorSnackbar,
  getSuccessSnackbar,
} from "../../../../../utils/utils";
import Input from "../../../../Input";
import LoadingOverlay from "../../../../LoadingOverlay";
import SideSheetDrawer from "../../../../SidesheetDrawer";

interface HcpcsCatalogDialogProps {
  dialogTitle: string;
  initialValues?: hcpcsCodeCatalogInitialValuesType;
  disclosureHost: DisclosureHost;
  uuid?: string;
}

const hcpcsCodeCatalogValidationSchema = Yup.object({
  hcpcs_code: Yup.string().trim().required().label("HCPCS Code"),
  description: Yup.string().trim().required().label("Description"),
});

const HcpcsCatalogDrawer = ({
  dialogTitle,
  initialValues,
  disclosureHost,
  uuid,
}: HcpcsCatalogDialogProps) => {
  const {
    values,
    resetForm,
    handleBlur,
    handleSubmit,
    handleChange,
    touched,
    errors,
  } = useFormik({
    initialValues: initialValues ?? HCPCS_CODE_CATALOG_INITIAL_VALUES,
    validationSchema: hcpcsCodeCatalogValidationSchema,
    onSubmit,
    enableReinitialize: true,
  });

  const { mutateAsync: addDiagnosisCode } = useAddCataLogCode();
  const { mutateAsync: editDiagnosisCode } = useUpdateCataLogCode();
  const queryClient = useQueryClient();
  const showSnackbar = useSnackbar();
  const [isLoading, setIsLoading] = useLoading();

  function resetAndClose() {
    resetForm();
    disclosureHost.close();
  }

  function onSubmit(values: hcpcsCodeCatalogInitialValuesType) {
    setIsLoading(true);

    const transformedValues = {
      code: values.hcpcs_code.trim(),
      description: values.description.trim(),
    };

    (initialValues
      ? editDiagnosisCode({
          values: { ...transformedValues, uuid, type: "HCPCS" },
        })
      : addDiagnosisCode({ values: { ...transformedValues, type: "HCPCS" } })
    )
      .then((res) => {
        showSnackbar(getSuccessSnackbar(res));

        queryClient.invalidateQueries({
          queryKey: ["catalog-diagnosis-code-import"],
        });

        resetAndClose();
      })
      .catch((err) => showSnackbar(getErrorSnackbar(err)))
      .finally(() => setIsLoading(false));
  }

  return (
    <SideSheetDrawer
      open={disclosureHost.opened}
      title={dialogTitle}
      onClose={resetAndClose}
      w="50vw"
      footerActions={
        <Button
          sx={{ width: "105px" }}
          onClick={() => handleSubmit()}
          variant="contained"
        >
          {dialogTitle?.includes("Edit") ? "Edit" : "Add"}
        </Button>
      }
    >
      <LoadingOverlay isLoading={isLoading} />

      <Grid container spacing={2} width="100%">
        <Grid item xs={12}>
          <Input
            required
            label="HCPCS Code"
            textFieldProps={{
              name: "hcpcs_code",
              value: values.hcpcs_code,
              onChange: handleChange,
              onBlur: handleBlur,
              placeholder: "Enter Code",
            }}
            error={touched.hcpcs_code && errors.hcpcs_code}
          />
        </Grid>
        <Grid item xs={12}>
          <Input
            required
            label="Description"
            textFieldProps={{
              name: "description",
              value: values.description,
              onChange: handleChange,
              onBlur: handleBlur,
              placeholder: "Description",
              multiline: true,
              rows: 4,
            }}
            error={touched.description && errors.description}
          />
        </Grid>
      </Grid>
    </SideSheetDrawer>
  );
};
export default HcpcsCatalogDrawer;
